<script setup lang="ts">
import { Subscribe } from '~/entities/subscribe';
import {
  CLIENT_API_NEWS_CATEGORIES_KEY,
  CLIENT_API_NEWS_CATEGORIES_URL,
  CLIENT_API_NEWS_MATERIALS_KEY,
  CLIENT_API_NEWS_MATERIALS_URL,
} from '~/shared/api/client/news/news.const';
import type {
  ClientApiNewsPaginatedResponse,
  INewsDataCategoriesClientDTO,
  INewsListingItemDataClientDTO,
} from '~/shared/api/client/news/news.dto';
import { CLIENT_NEWS_ON_PAGE } from '../';

import Pagination from '~/shared/ui/pagination/Pagination.vue';
import { SEO_TITLE } from '~/shared/lib/seo';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { updateNewsItemsSeo } from '~/shared/lib/updateNewsItemsSeo';
import { getAuthorName } from '~/shared/lib/getAuthorName';
import { getMaterialLink } from '~/shared/lib/getMaterialLink';

const props = defineProps<{
  newsType?: 'news' | 'analytics' | 'expert' | 'new_object' | '';
  categoryId?: number | null;
  authorId?: number | null;
  withExperts?: boolean;
  partner?: boolean;
}>();

const route = useRoute();
const router = useRouter();

const pageFromQuery = computed(() => {
  const page = Number(route.query.page);
  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
});

const isExpertNews = computed(() => props.newsType === 'expert');

const routeUrl = computed(() => CLIENT_API_NEWS_MATERIALS_URL);

const routeKey = computed(() => CLIENT_API_NEWS_MATERIALS_KEY);

const categoriesState = useState<INewsDataCategoriesClientDTO[]>(
  'client-news-categories',
  () => [],
);

const categoriesStatus = ref<'pending' | 'success' | 'error' | 'idle'>(
  'pending',
);

if (categoriesState.value.length === 0 || !isExpertNews.value) {
  categoriesStatus.value = 'pending';
  const { data } = await useCustomFetch<INewsDataCategoriesClientDTO[]>(
    CLIENT_API_NEWS_CATEGORIES_URL,
    {
      method: 'GET',
      key: CLIENT_API_NEWS_CATEGORIES_KEY,
      watch: false,
    },
  );
  if (data.value) {
    categoriesState.value = data.value;
  }
  categoriesStatus.value = 'success';
} else {
  categoriesStatus.value = 'success';
}

const categories = computed(() => categoriesState.value);

/*
  Партнёрский тег заводится миграцией и на каждом стенде получает свой id,
  поэтому раздел ищет его по флагу is_partner, а не по константе. Список
  категорий запрашивается выше и до запроса материалов, так что id готов
  вовремя и лишнего похода в сеть не появляется.
*/
const partnerCategoryId = computed(
  () => categories.value.find((item) => item.is_partner)?.id ?? null,
);

// Legacy /category/<partner id> links keep working: /partners is the canonical URL
if (
  !props.partner &&
  !props.newsType &&
  props.categoryId &&
  props.categoryId === partnerCategoryId.value
) {
  await navigateTo('/partners', { redirectCode: 301 });
}

const categoryID = computed(() =>
  props.partner ? partnerCategoryId.value : (props.categoryId ?? null),
);
const authorID = computed(() => props.authorId ?? null);
const experts = computed(() => (props.withExperts ? true : undefined));

const pageNumber = useState('client-news-page', () => pageFromQuery.value);

if (pageNumber.value !== pageFromQuery.value) {
  pageNumber.value = pageFromQuery.value;
}

const { data, status: newsStatus } = await useCustomFetch<
  ClientApiNewsPaginatedResponse<INewsListingItemDataClientDTO>
>(routeUrl.value, {
  key: routeKey.value,
  method: 'GET',
  watch: [categoryID, pageNumber],
  params: {
    page: pageNumber,
    page_size: CLIENT_NEWS_ON_PAGE,
    category: categoryID,
    author: authorID,
    search: '',
    type: props.newsType,
    experts,
  },
});

const category = computed(() =>
  categories.value.find((item) => item.id === categoryID.value),
);

const isPartnerCategory = computed(() => Boolean(category.value?.is_partner));

const authorNameComputed = computed(() =>
  getAuthorName(data.value?.results?.[0]?.author),
);

const title = computed(() => {
  let slug: string;

  switch (props.newsType) {
    case 'analytics':
      slug = 'Статьи';
      break;
    case 'expert':
      slug = 'Мнения экспертов';
      break;
    case '':
      slug = 'Материалы';
      break;
    default:
      slug = 'Новости';
      break;
  }

  let baseTitle: string;

  if (authorID.value) {
    baseTitle = `${slug} от «${authorNameComputed.value}»`;
  } else if (categoryID.value && category.value) {
    // The partner section is titled by its own name, without the category prefix
    baseTitle = isPartnerCategory.value
      ? category.value.name
      : `${slug} категории «${category.value.name}»`;
  } else {
    baseTitle = slug;
  }

  return baseTitle;
});

const seoTitle = computed(() => {
  const titleWithPage =
    pageNumber.value > 1
      ? `${title.value}. Страница ${pageNumber.value}`
      : title.value;

  return SEO_TITLE.replace('%%TITLE%%', titleWithPage);
});

const handleTabsClick = (id: number) => {
  if (id !== categoryID.value) {
    pageNumber.value = 1;
  }

  if (id === 0) {
    navigateTo(props.newsType ? `/${props.newsType}/` : '/category/');
  } else if (id === partnerCategoryId.value) {
    // The partner tag has its own route: /category/<id> would drop withExperts
    navigateTo('/partners');
  } else {
    navigateTo(`/category/${id}`);
  }
};

watch(
  () => pageFromQuery.value,
  (value) => {
    if (pageNumber.value !== value) pageNumber.value = value;
  },
);

watch(
  () => pageNumber.value,
  (value) => {
    if (!import.meta.client) return;

    const nextQuery = { ...route.query };

    if (value <= 1) {
      delete nextQuery.page;
    } else {
      nextQuery.page = String(value);
    }

    router.push({ path: route.path, query: nextQuery });

    nextTick(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  },
);

watch(
  () => data.value,
  (newValue) => {
    if (!import.meta.client) return;

    if (newValue) {
      updateNewsItemsSeo(newValue.results, seoTitle.value);
    }
  },
);

if (data.value?.results?.length && data.value?.results?.length > 0) {
  updateNewsItemsSeo(data.value.results, seoTitle.value);
}
</script>

<template>
  <div :class="$style.static__page">
    <PageHeader
      :title="title"
      :results="data?.count"
      :show-border-bottom="true"
    >
      <template v-if="isPartnerCategory" #title-aside>
        <PartnerDisclosure :category-name="category?.name ?? ''" />
      </template>
    </PageHeader>

    <Loader v-if="categoriesStatus === 'pending'" smallest />
    <NewsTabs
      v-else-if="!isExpertNews"
      :categories="categories"
      :selected="categoryID || 0"
      @category-selected="handleTabsClick"
    />

    <div :class="$style.news__wrapper">
      <Loader v-if="newsStatus === 'pending'" />

      <NewsItemCard
        v-for="(item, index) in data?.results"
        v-else-if="data?.results && data.results.length > 0"
        :key="`listing-news-${index}`"
        :title="item.title"
        :category="item?.category?.name || false"
        :category-link="
          item?.category ? `/category/${item.category?.id}` : false
        "
        :date="item.publication_date"
        :subject="item.subject"
        :link="getMaterialLink(item)"
        :views="item.views"
        :image="item.image?.photo.file_url"
        :new-tab="Boolean(item.redirect)"
      />

      <div
        v-else-if="data?.results && data.results.length === 0"
        :class="$style.noNews"
      >
        <div :class="$style.noNews__title">Новостей в этой категории нет</div>
      </div>

      <Pagination
        v-if="newsStatus === 'success'"
        theme="client"
        page-name="client-news-page"
        :total="data?.count || 0"
        :sibling-count="1"
        show-edges
        :items-per-page="CLIENT_NEWS_ON_PAGE"
        :default-page="pageNumber"
        @page-change="pageNumber = $event"
      />
      <div v-else-if="newsStatus === 'error'" :class="$style.noNews">
        <div :class="$style.noNews__title">Не удалось получить новости</div>
      </div>
    </div>

    <Subscribe :class="$style.subscribe" />
  </div>
</template>

<style module lang="scss">
.static__page {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
}

.news__wrapper {
  height: 100%;
  flex: 1;
  border-bottom: 1px solid var(--color-gray-line);
}

.noNews {
  padding: var(--space-2-5x);
  &__title {
    @include text_h5;
  }
}

@media (max-width: 479px) {
  .noNews {
    padding: var(--space-2x);
  }
}
</style>
